import axios from 'axios';
import toast from 'react-hot-toast';
import { api } from '@/lib/env';
import { getFileHash } from '@/lib/getFileHash';
import { fun } from '@/lib/request';
import { StateSlice } from '@/stores/appStore';
import { WrapResponse } from '@/types';
import {
  DEFAULT_IMAGE_MODEL,
  GENERATED_PROMPT_PREFIX_REGEX,
  normalizeImageModel,
  normalizeImageType,
  type ImageGenerationTask,
  type ImageUploadResult,
  type ResearchDrawingFormValue,
} from '@/features/research-drawing/types';

type RawWrapResponse<T> = {
  code: number;
  msg?: string;
  message?: string;
  data: T;
};

type ImageGenerationTaskListData = {
  total: number;
  page: number;
  list: ImageGenerationTask[];
};

type RawImageGenerationTask = {
  taskId?: number | string;
  task_id?: number | string;
  status?: -1 | 0 | 1 | 2;
  userInput?: string;
  user_input?: string;
  imageModel?: string;
  image_model?: string;
  imageType?: string;
  image_type?: string;
  previousImages?: string[] | string;
  previous_images?: string[] | string;
  researchDirection?: string;
  research_direction?: string;
  imageSize?: string;
  image_size?: string;
  aspectRatio?: string;
  aspect_ratio?: string;
  beansCost?: number;
  beans_cost?: number;
  imageUrl?: string;
  image_url?: string;
  createTime?: string;
  create_time?: string;
  finishTime?: string | null;
  finish_time?: string | null;
};

type RawImageGenerationTaskListData = {
  total?: number;
  page?: number;
  list?: RawImageGenerationTask[];
};

type RawDeleteTaskData = {
  deletedCount?: number;
  deleted_count?: number;
};

function normalizeHistoryTaskList(data: RawImageGenerationTaskListData, fallbackPage: number): ImageGenerationTaskListData {
  return {
    total: Number(data.total ?? 0),
    page: Number(data.page ?? fallbackPage),
    list: (data.list || []).map(normalizeHistoryTask),
  };
}

async function requestHistoryPage(page: number): Promise<ImageGenerationTaskListData> {
  const response = await fun.post<RawWrapResponse<RawImageGenerationTaskListData>>(
    api('/user/task', true),
    {
      taskType: 'image_gen',
      page,
    },
  );
  const responseData = response.data;
  if (responseData.code !== 0) {
    throw new Error(responseData.msg || responseData.message || '获取生成历史失败');
  }

  return normalizeHistoryTaskList(responseData.data || {}, page);
}

type UploadUrlItem = string | {
  uploadUrl?: string;
  upload_url?: string;
  objectName?: string;
  object_name?: string;
};

type UploadUrlResponse = {
  code: number;
  message?: string;
  msg?: string;
  data?: UploadUrlItem[];
};

const OSS_BASE_URL = 'https://fd_web.oss-cn-hangzhou.aliyuncs.com';

function getPublicUploadUrl(item: UploadUrlItem) {
  if (typeof item === 'string') {
    return item.split('?')[0];
  }

  const objectName = item.objectName || item.object_name;
  if (objectName) {
    return `${OSS_BASE_URL}/${objectName.replace(/^\/+/, '')}`;
  }

  return (item.uploadUrl || item.upload_url || '').split('?')[0];
}

function getSignedUploadUrl(item: UploadUrlItem) {
  if (typeof item === 'string') {
    return item;
  }

  return item.uploadUrl || item.upload_url || '';
}

export type ResearchDrawingSlice = {
  researchDrawing: {
    formValue: ResearchDrawingFormValue;
    previewImageUrl: string;
    previewTaskId?: number;
    history: {
      total: number;
      page: number;
      list: ImageGenerationTask[];
      loading: boolean;
    };
    uploadingReferenceImage: boolean;
    setFormValue: (value: Partial<ResearchDrawingFormValue> | ((prev: ResearchDrawingFormValue) => ResearchDrawingFormValue)) => void;
    resetFormValue: () => void;
    setPreview: (preview: { imageUrl?: string; taskId?: number }) => void;
    fetchHistory: (page?: number) => Promise<void>;
    findHistoryTask: (taskId: number) => Promise<ImageGenerationTask | null>;
    deleteHistoryTask: (taskId: number) => Promise<boolean>;
    uploadReferenceImage: (file: File) => Promise<ImageUploadResult | null>;
    loadHistoryTaskToForm: (task: ImageGenerationTask) => void;
  };
};

export const researchDrawingDefaultFormValue: ResearchDrawingFormValue = {
  userInput: '',
  modifyInstruction: '',
  researchDirection: '',
  imageModel: DEFAULT_IMAGE_MODEL,
  imageType: 'mechanism_diagram',
  previousImagesMode: 'none',
  previousImages: [],
  imageSize: '1K',
  aspectRatio: '1:1',
};

function stripGeneratedPromptPrefix(value: string) {
  return value.replace(GENERATED_PROMPT_PREFIX_REGEX, '');
}

function isOriginalGenerationTask(task: ImageGenerationTask) {
  return GENERATED_PROMPT_PREFIX_REGEX.test(task.userInput || '');
}

function getHistoryTaskDisplayInput(task: ImageGenerationTask) {
  if (isOriginalGenerationTask(task)) {
    return task.researchDirection || stripGeneratedPromptPrefix(task.userInput || '');
  }

  return task.userInput || task.researchDirection || '';
}

function isModifiedGenerationTask(task: ImageGenerationTask) {
  return Boolean((task.researchDirection || '').trim());
}

function normalizeStringArray(value?: string[] | string) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter(Boolean);
    }
  } catch {
    // Some environments may return a single URL string instead of JSON.
  }

  return [value].filter(Boolean);
}

function normalizeHistoryTask(task: RawImageGenerationTask): ImageGenerationTask {
  return {
    taskId: Number(task.taskId ?? task.task_id ?? 0),
    status: task.status ?? 0,
    userInput: task.userInput ?? task.user_input ?? '',
    imageModel: normalizeImageModel(task.imageModel ?? task.image_model),
    imageType: normalizeImageType(task.imageType ?? task.image_type),
    previousImages: normalizeStringArray(task.previousImages ?? task.previous_images),
    researchDirection: task.researchDirection ?? task.research_direction ?? '',
    imageSize: task.imageSize ?? task.image_size ?? '',
    aspectRatio: task.aspectRatio ?? task.aspect_ratio ?? '',
    beansCost: Number(task.beansCost ?? task.beans_cost ?? 0),
    imageUrl: task.imageUrl ?? task.image_url ?? '',
    createTime: task.createTime ?? task.create_time ?? '',
    finishTime: task.finishTime ?? task.finish_time ?? null,
  };
}

export const createResearchDrawingSlice: StateSlice<ResearchDrawingSlice> = (set, get) => ({
  researchDrawing: {
    formValue: researchDrawingDefaultFormValue,
    previewImageUrl: '',
    previewTaskId: undefined,
    history: {
      total: 0,
      page: 1,
      list: [],
      loading: false,
    },
    uploadingReferenceImage: false,
    setFormValue: (value) => {
      set(state => {
        state.researchDrawing.formValue = typeof value === 'function'
          ? value(state.researchDrawing.formValue)
          : {
            ...state.researchDrawing.formValue,
            ...value,
          };
      });
    },
    resetFormValue: () => {
      set(state => {
        state.researchDrawing.formValue = researchDrawingDefaultFormValue;
        state.researchDrawing.previewImageUrl = '';
        state.researchDrawing.previewTaskId = undefined;
      });
    },
    setPreview: ({ imageUrl = '', taskId }) => {
      set(state => {
        state.researchDrawing.previewImageUrl = imageUrl;
        state.researchDrawing.previewTaskId = taskId;
      });
    },
    fetchHistory: async (page = 1) => {
      set(state => {
        state.researchDrawing.history.loading = true;
      });
      try {
        const data = await requestHistoryPage(page);
        set(state => {
          state.researchDrawing.history.total = data.total;
          state.researchDrawing.history.page = data.page;
          state.researchDrawing.history.list = data.list;
        });
      } catch (error) {
        console.error('Error fetch image generation tasks:', error);
        toast.error(error instanceof Error ? error.message : '获取生成历史失败');
      } finally {
        set(state => {
          state.researchDrawing.history.loading = false;
        });
      }
    },
    findHistoryTask: async (taskId) => {
      const firstPage = await requestHistoryPage(1);
      const taskInFirstPage = firstPage.list.find(task => task.taskId === taskId);
      if (taskInFirstPage) {
        return taskInFirstPage;
      }

      const pageSize = firstPage.list.length;
      const totalPages = pageSize > 0 ? Math.ceil(firstPage.total / pageSize) : 1;
      for (let page = 2; page <= totalPages; page += 1) {
        const historyPage = await requestHistoryPage(page);
        const task = historyPage.list.find(item => item.taskId === taskId);
        if (task) {
          return task;
        }
      }

      return null;
    },
    deleteHistoryTask: async (taskId) => {
      try {
        const response = await fun.post<RawWrapResponse<RawDeleteTaskData>>(
          api('/user/task/delete', true),
          {
            taskIds: [taskId],
          },
        );
        const responseData = response.data;
        if (responseData.code !== 0) {
          toast.error(responseData.msg || responseData.message || '删除生成记录失败');
          return false;
        }

        const deletedCount = Number(responseData.data?.deletedCount ?? responseData.data?.deleted_count ?? 0);
        if (deletedCount > 0) {
          set(state => {
            state.researchDrawing.history.list = state.researchDrawing.history.list.filter(task => task.taskId !== taskId);
            state.researchDrawing.history.total = Math.max(0, state.researchDrawing.history.total - deletedCount);
            if (state.researchDrawing.previewTaskId === taskId) {
              state.researchDrawing.previewImageUrl = '';
              state.researchDrawing.previewTaskId = undefined;
              state.researchDrawing.formValue.taskId = undefined;
            }
          });
          toast.success('删除成功');
          return true;
        } else {
          toast('未删除任何记录');
          return false;
        }
      } catch (error) {
        console.error('Error delete image generation task:', error);
        toast.error('删除生成记录失败');
        return false;
      }
    },
    uploadReferenceImage: async (file) => {
      set(state => {
        state.researchDrawing.uploadingReferenceImage = true;
      });
      try {
        const contentType = file.type || 'image/png';
        const md5 = await getFileHash(file, 'MD5');
        const sha1 = await getFileHash(file, 'SHA1');
        const uploadData = (await fun.post<UploadUrlResponse>(
          api('/upload/getUploadUrl', true),
          {
            scene: 'generate',
            files: [
              {
                md5,
                sha1,
                'content-type': contentType,
              },
            ],
          },
        )).data;

        if (uploadData.code !== 0) {
          toast.error(uploadData.message || uploadData.msg || '获取上传地址失败');
          return null;
        }

        const uploadItem = uploadData.data?.[0];
        if (!uploadItem) {
          toast.error('获取上传地址失败');
          return null;
        }

        const uploadUrl = getSignedUploadUrl(uploadItem);
        if (!uploadUrl) {
          toast.error('获取上传地址失败');
          return null;
        }

        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': contentType,
          },
        });

        const result = {
          url: getPublicUploadUrl(uploadItem),
          filename: file.name,
        };
        set(state => {
          state.researchDrawing.formValue.previousImagesMode = 'upload';
          state.researchDrawing.formValue.previousImages = [result.url];
        });
        toast.success('参考图上传成功');
        return result;
      } catch (error) {
        console.error('Error upload reference image:', error);
        toast.error(error instanceof Error ? error.message : '参考图上传失败');
        return null;
      } finally {
        set(state => {
          state.researchDrawing.uploadingReferenceImage = false;
        });
      }
    },
    loadHistoryTaskToForm: (task) => {
      const imageSize = task.imageSize?.toUpperCase();
      const aspectRatio = task.aspectRatio;
      const previousImages = task.previousImages || [];
      const isModifiedTask = isModifiedGenerationTask(task);
      set(state => {
        state.researchDrawing.formValue = {
          userInput: isModifiedTask ? '' : getHistoryTaskDisplayInput(task),
          modifyInstruction: isModifiedTask ? task.userInput || '' : '',
          researchDirection: isModifiedTask ? task.researchDirection || '' : '',
          imageModel: normalizeImageModel(task.imageModel),
          imageType: normalizeImageType(task.imageType),
          previousImagesMode: isModifiedTask || previousImages.length ? 'upload' : 'none',
          previousImages: isModifiedTask ? (task.imageUrl ? [task.imageUrl] : []) : previousImages,
          imageSize: imageSize === '2K' || imageSize === '4K' ? imageSize : '1K',
          aspectRatio: aspectRatio === '16:9' || aspectRatio === '4:3' || aspectRatio === '3:4' ? aspectRatio : '1:1',
          taskId: task.taskId,
        };
        state.researchDrawing.previewImageUrl = task.imageUrl || '';
        state.researchDrawing.previewTaskId = task.taskId;
      });
    },
  },
});
